/**
 * Authentication endpoints, one function per HTTP call.
 *
 * The signatures follow `AuthController` exactly: routes, verbs, query parameters and body shapes. Two details are
 * worth stating because they are easy to get wrong:
 *
 * - **the refresh token is never passed through JavaScript**; every call below relies on the `HttpOnly` cookie
 *   (that is why the refresh and logout bodies are empty objects rather than payloads);
 * - **`forgot-password` answers 202 with no body**, and `resend-verification` deliberately reports the same result
 *   for a registered and an unregistered address, so the SPA must not present the answer as proof of existence.
 */

import { apiClient } from '@/lib/apiClient';
import { env } from '@/lib/env';
import type {
  AuthPolicy,
  AuthSession,
  ChangePasswordResult,
  ExternalIdentity,
  OrganisationClientConfig,
  RegisterUserResult,
  ResendVerificationResult,
  ResetPasswordResult,
  RevokeSessionResult,
  SessionInfo,
  UserProfile,
  VerificationChannel,
  VerificationState,
  VerifyCodeResult,
  VerifyEmailResult,
} from '@/types/api';

/** Body of `POST /auth/organisation/session` (TASK-0064). */
export interface OrganisationSessionPayload {
  /** The ID token the authority issued to the SPA client at the end of the PKCE flow. */
  idToken: string;
  /** The access token the SPA presents; the backend reads the UserInfo endpoint with it. */
  accessToken: string;
}

/** Body of `POST /auth/login`. */
export interface LoginPayload {
  /** E-mail address or mobile number the account registered with. */
  identifier: string;
  password: string;
  rememberMe: boolean;
}

/** Body of `POST /auth/register`. */
export interface RegisterPayload {
  /** Address on the e-mail channel, or `undefined` when the policy requires a mobile number. */
  email?: string;
  /** Number on the mobile channel, or `undefined` on the e-mail channel. */
  mobileNumber?: string;
  /** Channel the account registers with; omitted lets the API apply the policy in force. */
  channel?: VerificationChannel;
  displayName: string;
  /** Public handle the account registers with (TASK-0060); required by the API. */
  publicIdentifier: string;
  password: string;
  acceptTerms: boolean;
}

/** Body of `POST /auth/reset-password`. */
export interface ResetPasswordPayload {
  token: string;
  newPassword: string;
}

/** Body of `POST /users/me/change-password`. */
export interface ChangePasswordPayload {
  currentPassword: string;
  newPassword: string;
}

/**
 * Body of `POST /auth/verify` (TASK-0062): a code for the identifier on either channel.
 *
 * The contract carries the **full** identifier (`email` or `mobileNumber`, exactly one), not the masked destination the verification
 * object publishes: the code is compared against the challenge of that account, and the masked form exists only so a browser payload
 * never repeats an address in full.
 */
export interface VerifyCodePayload {
  /** Address the code was sent to, on the e-mail channel. */
  email?: string;
  /** Number the code was sent to, on the mobile channel. */
  mobileNumber?: string;
  /** The typed code, digits only. */
  code: string;
  /** Channel the code belongs to, so a code never redeems the other identifier. */
  channel?: VerificationChannel;
}

/** Body of `POST /auth/verify/resend` (TASK-0062); the answer is a neutral `202`. */
export interface ResendVerificationPayload {
  /** Address the account registered with, on the e-mail channel. */
  email?: string;
  /** Number the account registered with, on the mobile channel. */
  mobileNumber?: string;
  /** Channel whose challenge is requested; omitted means the channel the policy uses. */
  channel?: VerificationChannel;
}

/** Authentication and account-recovery operations. */
export const authApi = {
  /** Authenticates an account; the API also sets the refresh-token cookie. */
  async login(payload: LoginPayload): Promise<AuthSession> {
    const { data } = await apiClient.post<AuthSession>('/auth/login', payload);
    return data;
  },

  /** Creates an account; answering 201 means the verification e-mail was dispatched. */
  async register(payload: RegisterPayload): Promise<RegisterUserResult> {
    const { data } = await apiClient.post<RegisterUserResult>('/auth/register', payload);
    return data;
  },

  /** Revokes the calling device's token family (or every device) and clears the cookie. */
  async logout(allDevices = false): Promise<void> {
    await apiClient.post('/auth/logout', {}, { params: { allDevices } });
  },

  /** Confirms the e-mail address using the token from the verification link. */
  async verifyEmail(token: string): Promise<VerifyEmailResult> {
    const { data } = await apiClient.post<VerifyEmailResult>('/auth/verify-email', { token });
    return data;
  },

  /** Requests a fresh verification e-mail. */
  async resendVerification(email: string): Promise<ResendVerificationResult> {
    const { data } = await apiClient.post<ResendVerificationResult>('/auth/resend-verification', {
      email,
    });
    return data;
  },

  /** Starts the password-reset flow; the answer is identical for known and unknown addresses. */
  async forgotPassword(email: string): Promise<void> {
    await apiClient.post('/auth/forgot-password', { email });
  },

  /** Completes the password-reset flow and revokes every existing session. */
  async resetPassword(payload: ResetPasswordPayload): Promise<ResetPasswordResult> {
    const { data } = await apiClient.post<ResetPasswordResult>('/auth/reset-password', payload);
    return data;
  },

  /** Changes the password of the signed-in account. */
  async changePassword(payload: ChangePasswordPayload): Promise<ChangePasswordResult> {
    const { data } = await apiClient.post<ChangePasswordResult>(
      '/users/me/change-password',
      payload,
    );
    return data;
  },

  /** Lists the active device sessions of the signed-in account. */
  async listSessions(): Promise<SessionInfo[]> {
    const { data } = await apiClient.get<SessionInfo[]>('/auth/sessions');
    return data;
  },

  /** Revokes one device session; `wasCurrentSession` tells the caller to clear its own tokens. */
  async revokeSession(familyId: string): Promise<RevokeSessionResult> {
    const { data } = await apiClient.delete<RevokeSessionResult>(`/auth/sessions/${familyId}`);
    return data;
  },

  /** Reads the profile of the signed-in account (used to re-validate a restored session). */
  async me(): Promise<UserProfile> {
    const { data } = await apiClient.get<UserProfile>('/users/me');
    return data;
  },

  /**
   * Registers the public identifier of the signed-in account, once (TASK-0060).
   *
   * A route of its own and not a member of `PUT /users/me`: the profile form saves repeatedly, and a handle that
   * travelled with it would either be renamed by a second save or have to be ignored. A second call answers `403`.
   */
  async registerPublicIdentifier(publicIdentifier: string): Promise<UserProfile> {
    const { data } = await apiClient.post<UserProfile>('/users/me/public-identifier', {
      publicIdentifier,
    });

    return data;
  },

  /**
   * Reads the registration and verification policy in force (TASK-0062).
   *
   * Anonymous and cacheable: the register screen must know which identifier to ask for before anybody signs in, and the answer
   * carries no secret. It is the **only** source the screens use to decide between e-mail and mobile — a client that guessed would
   * disagree with the API the first time an administrator switched the channel.
   */
  async getAuthPolicy(): Promise<AuthPolicy> {
    const { data } = await apiClient.get<AuthPolicy>('/auth/policy');
    return data;
  },

  /** Redeems a one-time code for the caller's identifier, on either channel. */
  async verifyCode(payload: VerifyCodePayload): Promise<VerifyCodeResult> {
    const { data } = await apiClient.post<VerifyCodeResult>('/auth/verify', payload);
    return data;
  },

  /**
   * Requests another verification challenge.
   *
   * The answer is the same `202` for a registered and an unregistered identifier, so the screen must never present it as proof that
   * an account exists; a call inside the cooldown is a `429` carrying `Retry-After`.
   */
  async resendVerificationChallenge(
    payload: ResendVerificationPayload,
  ): Promise<VerificationState> {
    const { data } = await apiClient.post<VerificationState>('/auth/verify/resend', payload);
    return data;
  },

  /** Registers the mobile number a pending account still has to prove. */
  async registerMobile(mobileNumber: string): Promise<VerificationState> {
    const { data } = await apiClient.post<VerificationState>('/auth/mobile', { mobileNumber });
    return data;
  },

  /**
   * Builds the URL that starts an external sign-in.
   *
   * A **navigation**, not a request: the API answers `302` to the provider, and a cross-origin redirect cannot be followed by
   * `fetch`. The caller assigns the result to `window.location`, which is why it is a plain function rather than a request.
   *
   * @param provider - Provider name from `GET /auth/policy`.
   * @param options - Return path and whether this is an explicit link from a signed-in owner.
   * @returns The absolute API URL to navigate to.
   */
  externalChallengeUrl(
    provider: string,
    options: { returnPath?: string; link?: boolean } = {},
  ): string {
    // The API base is whatever the deployment configured (TASK-0076): an absolute address or a relative `/api/v1`.
    // `new URL` resolves both against the *page*, which is what a navigation needs — a relative base has no host of its
    // own, and a redirect is followed by the address bar, not by the API client.
    const base = env.apiBaseUrl.replace(/\/$/, '');
    const params = new URLSearchParams();

    if (options.returnPath) {
      params.set('returnPath', options.returnPath);
    }

    if (options.link) {
      params.set('link', 'true');
    }

    const query = params.toString();
    const path = `${base}/auth/external/${encodeURIComponent(provider)}/challenge${query ? `?${query}` : ''}`;

    return new URL(path, globalThis.location?.origin ?? base).toString();
  },

  /**
   * Exchanges the ticket a completed external sign-in produced for a session.
   *
   * The ticket is single-use and lives a minute; the API also sets the refresh-token cookie, exactly as `login` does.
   */
  async exchangeExternalTicket(ticket: string): Promise<AuthSession> {
    const { data } = await apiClient.post<AuthSession>('/auth/external/session', { ticket });
    return data;
  },

  /** Attaches a parked provider identity to the signed-in account. */
  async linkExternalIdentity(ticket: string): Promise<ExternalIdentity> {
    const { data } = await apiClient.post<ExternalIdentity>('/auth/external/link', { ticket });
    return data;
  },

  /** Lists the provider identities attached to the signed-in account. */
  async listExternalIdentities(): Promise<ExternalIdentity[]> {
    const { data } = await apiClient.get<ExternalIdentity[]>('/users/me/external-identities');
    return data;
  },

  /** Detaches one provider identity; the API refuses when it would remove the last way in. */
  async unlinkExternalIdentity(identityId: string, reason?: string): Promise<void> {
    await apiClient.delete(`/users/me/external-identities/${identityId}`, {
      params: reason ? { reason } : undefined,
    });
  },

  /**
   * Reads the public OIDC client configuration of the organisation sign-in (TASK-0064).
   *
   * Anonymous and credential-free: a public client's identifier and callback URI travel in the authorization request the
   * browser makes. The API answers `404 external_login_unavailable` when the method is switched off or the provider is
   * unconfigured, which the caller surfaces as "unavailable" rather than retrying.
   */
  async getOrganisationClientConfig(): Promise<OrganisationClientConfig> {
    const { data } = await apiClient.get<OrganisationClientConfig>('/auth/organisation/client');
    return data;
  },

  /**
   * Establishes the Prompt Forge session from a completed organisation OIDC callback (TASK-0064).
   *
   * The tokens the SPA's OIDC client validated are presented in a POST body — never in a URL — and the backend validates
   * them server-side before it resolves or provisions an account. The API also sets the refresh-token cookie, exactly as
   * `login` does.
   */
  async establishOrganisationSession(payload: OrganisationSessionPayload): Promise<AuthSession> {
    const { data } = await apiClient.post<AuthSession>('/auth/organisation/session', payload);
    return data;
  },
};
